// Snap-to-grid window manager for the ladder panels: fixed-size cells, no auto-packing, no overlap.

export interface GridRect { x: number; y: number; w: number; h: number }

/** Default cell size in px; adjustable in Settings. Layouts are saved in cells, so they record the size they used. */
export const DEFAULT_CELL = 43;
export const CELL_RANGE = { min: 24, max: 96 };
/** Cell size layouts were saved at before it became adjustable. */
export const LEGACY_CELL = 64;
/** Current cell size; `--cell` in styles.css follows it so the dot grid lands on the cells panels snap to. */
let CELL = DEFAULT_CELL;
const GAP = 6;
// Half the gutter off each side (not all off the right) centres a panel between the dot lattice.
const INSET = GAP / 2;
// Size limits are in pixels so they mean the same at any cell size.
const MIN_W_PX = 320; // the 128px left rail plus a usable slice of ladder
const MIN_H_PX = 256;
const DEFAULT_W_PX = 384;
const DEFAULT_H_PX = 384;
const minW = () => Math.ceil(MIN_W_PX / CELL);
const minH = () => Math.ceil(MIN_H_PX / CELL);

export function cellPx(): number {
  return CELL;
}

/** A rect saved at one cell size, re-expressed at another so it keeps its on-screen size and place. */
export function scaleRect(r: GridRect, from: number, to: number): GridRect {
  const k = from / to;
  return { x: Math.round(r.x * k), y: Math.round(r.y * k), w: Math.round(r.w * k), h: Math.round(r.h * k) };
}

interface Item {
  id: string;
  rect: GridRect;
  el: HTMLDivElement;
  body: HTMLDivElement;
  /** Venue glyph ahead of the title — K, P — so a mixed grid scans at a glance. */
  venue: HTMLSpanElement;
  title: HTMLSpanElement;
}

export interface GridOpts {
  /** Asked before a panel is removed; return false to veto. */
  onRemove: (id: string) => boolean;
  /** The item's pixel size changed — anything measuring itself must remeasure. */
  onResized: (id: string) => void;
  /** Any commit worth persisting. */
  onChange: () => void;
}

export class GridLayout {
  private items: Item[] = [];
  private ph: HTMLDivElement;
  private cols = 1;

  constructor(private host: HTMLElement, private opts: GridOpts) {
    this.host.classList.add('gridwrap');
    this.ph = document.createElement('div');
    this.ph.className = 'gi-ph';
    this.ph.style.display = 'none';
    this.host.appendChild(this.ph);

    this.cols = this.colCount();
    window.addEventListener('resize', () => this.reflow());
  }

  private colCount(): number {
    // n columns need n*CELL - INSET px: the trailing inset is inside the last cell.
    return Math.max(minW(), Math.floor((this.host.clientWidth + INSET) / CELL));
  }

  /** Adds a panel. Returns false when the grid has no room for it. */
  add(id: string, content: HTMLElement, rect?: GridRect): boolean {
    const r = rect ? this.fit(rect) : this.findSlot(Math.round(DEFAULT_W_PX / CELL), Math.round(DEFAULT_H_PX / CELL));
    if (!r) return false;

    const el = document.createElement('div');
    el.className = 'gi';
    el.innerHTML = `
<div class="gi-bar">
  <span class="gi-grip">⠿</span>
  <span class="gi-venue"></span>
  <span class="gi-title"></span>
  <button class="gi-x" title="Remove this ladder">✕</button>
</div>
<div class="gi-body"></div>
<div class="gi-rs" title="Drag to resize"></div>
<div class="gi-rs-w" title="Drag to resize"></div>`;

    const body = el.querySelector('.gi-body') as HTMLDivElement;
    body.appendChild(content);
    const item: Item = {
      id, rect: { ...r }, el, body,
      venue: el.querySelector('.gi-venue') as HTMLSpanElement,
      title: el.querySelector('.gi-title') as HTMLSpanElement,
    };

    el.querySelector('.gi-x')!.addEventListener('click', () => {
      if (this.opts.onRemove(id)) this.remove(id);
    });
    (el.querySelector('.gi-bar') as HTMLElement).addEventListener(
      'pointerdown', (ev) => this.startDrag(item, ev as PointerEvent),
    );
    (el.querySelector('.gi-rs') as HTMLElement).addEventListener(
      'pointerdown', (ev) => this.startResize(item, ev as PointerEvent, 'e'),
    );
    (el.querySelector('.gi-rs-w') as HTMLElement).addEventListener(
      'pointerdown', (ev) => this.startResize(item, ev as PointerEvent, 'w'),
    );

    this.items.push(item);
    this.host.appendChild(el);
    this.place(item);
    this.growHost();
    this.opts.onChange();
    return true;
  }

  remove(id: string): void {
    const i = this.items.findIndex((it) => it.id === id);
    if (i < 0) return;
    this.items[i].el.remove();
    this.items.splice(i, 1);
    this.growHost();
    this.opts.onChange();
  }

  setTitle(id: string, text: string): void {
    const it = this.items.find((x) => x.id === id);
    if (!it) return;
    it.title.textContent = text;
    // A bare identifier (a ticker or slug) copies on click; placeholders and arb pair labels do not.
    if (text !== 'empty' && /^\S+$/.test(text)) it.title.dataset.copy = text;
    else delete it.title.dataset.copy;
  }

  /** The glyph before the title; `title` is its hover text. */
  setVenue(id: string, glyph: string, title: string): void {
    const it = this.items.find((x) => x.id === id);
    if (!it) return;
    it.venue.textContent = glyph;
    it.venue.title = title;
  }

  rects(): { id: string; rect: GridRect }[] {
    return this.items.map((it) => ({ id: it.id, rect: { ...it.rect } }));
  }

  count(): number {
    return this.items.length;
  }

  /** Coerces a requested rect into a legal one, keeping its size if possible. */
  private fit(r: GridRect): GridRect | null {
    const w = clamp(Math.round(r.w), minW(), this.cols);
    const h = Math.max(minH(), Math.round(r.h));
    const want = { x: clamp(Math.round(r.x), 0, this.cols - w), y: Math.max(0, Math.round(r.y)), w, h };
    return this.collides(want, null) ? this.findSlot(w, h) : want;
  }

  /** A slot for a new panel, scanning left-to-right then down. */
  findSlot(w: number, h: number): GridRect | null {
    for (let y = 0; y < 200; y++) {
      for (let x = 0; x + w <= this.cols; x++) {
        const r = { x, y, w, h };
        if (!this.collides(r, null)) return r;
      }
    }
    return null;
  }

  /** First legal rect from candidates; a gesture blocked on one axis still moves on the other. */
  private firstLegal(skip: Item, cands: GridRect[]): GridRect | null {
    return cands.find((c) => !this.collides(c, skip)) ?? null;
  }

  private collides(r: GridRect, skip: Item | null): boolean {
    return this.items.some((it) => it !== skip
      && r.x < it.rect.x + it.rect.w && it.rect.x < r.x + r.w
      && r.y < it.rect.y + it.rect.h && it.rect.y < r.y + r.h);
  }

  /** Pixel offset of a cell's leading edge, and its inverse. */
  private static toPx(cell: number): number {
    return cell * CELL + INSET;
  }

  private static toCell(px: number): number {
    return Math.round((px - INSET) / CELL);
  }

  private box(el: HTMLElement, r: GridRect): void {
    el.style.left = `${GridLayout.toPx(r.x)}px`;
    el.style.top = `${GridLayout.toPx(r.y)}px`;
    el.style.width = `${r.w * CELL - GAP}px`;
    el.style.height = `${r.h * CELL - GAP}px`;
  }

  private place(it: Item): void {
    this.box(it.el, it.rect);
  }

  private showPh(r: GridRect): void {
    this.ph.style.display = '';
    this.box(this.ph, r);
  }

  /** Enough room for the lowest panel plus a blank row to drop into. */
  private growHost(): void {
    const bottom = this.items.reduce((m, it) => Math.max(m, it.rect.y + it.rect.h), 0);
    this.host.style.height = `${(bottom + 2) * CELL}px`;
  }

  /** Change the cell size, keeping every panel at about the same pixel size and place. */
  setCell(px: number): void {
    const next = Math.min(CELL_RANGE.max, Math.max(CELL_RANGE.min, Math.round(px)));
    document.documentElement.style.setProperty('--cell', `${next}px`);
    if (next === CELL) return;
    const prev = CELL;
    CELL = next;
    this.cols = this.colCount();
    // Re-place top-left first, each against those already placed, so rounding can't leave two overlapping.
    const order = [...this.items].sort((a, b) => a.rect.y - b.rect.y || a.rect.x - b.rect.x);
    this.items = [];
    for (const it of order) {
      it.rect = this.fit(scaleRect(it.rect, prev, next)) ?? this.findSlot(minW(), minH()) ?? { x: 0, y: 0, w: minW(), h: minH() };
      this.items.push(it);
      this.place(it);
      this.opts.onResized(it.id);
    }
    this.growHost();
    this.opts.onChange();
  }

  /** Re-fits every panel to the current width; called on resize and also when the sidebar opens/closes. */
  reflow(): void {
    const cols = this.colCount();
    if (cols === this.cols) return;
    this.cols = cols;
    for (const it of this.items) {
      if (it.rect.w > cols) it.rect.w = Math.max(minW(), cols);
      if (it.rect.x + it.rect.w > cols) it.rect.x = Math.max(0, cols - it.rect.w);
      this.place(it);
    }
    this.growHost();
    for (const it of this.items) this.opts.onResized(it.id);
    this.opts.onChange();
  }

  private startDrag(it: Item, ev: PointerEvent): void {
    if ((ev.target as HTMLElement).closest('button')) return;
    ev.preventDefault();

    const origin = { ...it.rect };
    let pending = { ...it.rect };
    const startX = ev.clientX;
    const startY = ev.clientY;
    const baseLeft = GridLayout.toPx(it.rect.x);
    const baseTop = GridLayout.toPx(it.rect.y);

    this.beginGesture(it);
    this.showPh(pending);

    const move = (e: PointerEvent) => {
      const left = baseLeft + (e.clientX - startX);
      const top = baseTop + (e.clientY - startY);
      it.el.style.left = `${left}px`;
      it.el.style.top = `${Math.max(INSET, top)}px`;

      const cand: GridRect = {
        x: clamp(GridLayout.toCell(left), 0, this.cols - pending.w),
        y: Math.max(0, GridLayout.toCell(top)),
        w: pending.w,
        h: pending.h,
      };
      // An illegal cell leaves the marker where it last was, so releasing always lands somewhere legal.
      const next = this.firstLegal(it, [cand, { ...cand, y: pending.y }, { ...cand, x: pending.x }]);
      if (next) {
        pending = next;
        this.showPh(pending);
      }
    };

    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      it.rect = this.collides(pending, it) ? origin : pending;
      this.endGesture(it);
      this.growHost();
      this.opts.onChange();
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  /** Resizing west moves the left edge too, keeping the far edge (x + w) fixed. */
  private startResize(it: Item, ev: PointerEvent, side: 'e' | 'w' = 'e'): void {
    ev.preventDefault();
    ev.stopPropagation();

    const origin = { ...it.rect };
    let pending = { ...it.rect };
    const startX = ev.clientX;
    const startY = ev.clientY;
    const baseW = it.rect.w * CELL - GAP;
    const baseH = it.rect.h * CELL - GAP;
    const baseLeft = GridLayout.toPx(it.rect.x);
    const right = origin.x + origin.w;

    this.beginGesture(it);
    this.showPh(pending);

    const move = (e: PointerEvent) => {
      const dx = e.clientX - startX;
      const wpx = Math.max(CELL, side === 'e' ? baseW + dx : baseW - dx);
      const hpx = Math.max(CELL, baseH + (e.clientY - startY));
      it.el.style.width = `${wpx}px`;
      it.el.style.height = `${hpx}px`;
      if (side === 'w') it.el.style.left = `${baseLeft + (baseW - wpx)}px`;

      const w = clamp(
        Math.round((wpx + GAP) / CELL), minW(), side === 'e' ? this.cols - pending.x : right,
      );
      const cand: GridRect = {
        x: side === 'e' ? pending.x : right - w,
        y: pending.y,
        w,
        h: Math.max(minH(), Math.round((hpx + GAP) / CELL)),
      };
      const keepW = { ...cand, w: pending.w, x: side === 'e' ? cand.x : pending.x };
      const next = this.firstLegal(it, [cand, { ...cand, h: pending.h }, keepW]);
      if (next) {
        pending = next;
        this.showPh(pending);
      }
    };

    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      it.rect = this.collides(pending, it) ? origin : pending;
      this.endGesture(it);
      this.growHost();
      this.opts.onResized(it.id);
      this.opts.onChange();
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  private beginGesture(it: Item): void {
    it.el.classList.add('gi-active');
    document.body.classList.add('gi-dragging');
  }

  private endGesture(it: Item): void {
    it.el.classList.remove('gi-active');
    document.body.classList.remove('gi-dragging');
    this.ph.style.display = 'none';
    this.place(it);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
